import { useEffect, useState } from 'react'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-provider'
import { Input } from '@/components/ui/input'
import { MarkdownContent } from '@/components/markdown/markdown-content'
import { MarkdownEditor } from '@/components/markdown/markdown-editor'
import { StepDragHandle } from './step-drag-handle'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { MaterialPicker } from '@/components/projects/material-picker'
import { ToolPicker } from '@/components/projects/tool-picker'
import { StepKindBadge } from '@/components/projects/step-kind-badge'
import { MilestoneBomStrip } from '@/components/projects/milestone-bom-strip'
import { ProjectPicker } from '@/components/projects/project-picker'
import { DependencyBadge } from '@/components/projects/dependency-badge'
import { DependencyPicker } from '@/components/projects/dependency-picker'
import { RefreshReferenceDialog } from '@/components/projects/refresh-reference-dialog'
import {
  useCreateStep,
  useDeleteStep,
  useInsertProjectReference,
  useRefreshFromSource,
  useUpdateStep,
} from '@/features/projects/use-steps'
import {
  useDetachMaterial,
  useDetachTool,
  useUpdateStepMaterial,
} from '@/features/projects/use-step-attachments'
import { useStepMoves, type StepCoords } from '@/features/projects/use-step-moves'
import { StepPhotoGrid } from './step-photo-grid'
import type { ProjectCatalog, StepNode } from '@/api/types'

interface StepRowProps {
  coords: StepCoords
  projectId: string
  /** Top-level steps for the entire current project — needed by ProjectPicker for cycle exclusion. */
  allSteps: StepNode[]
  readOnly: boolean
  catalog: ProjectCatalog
}

const TITLE_PLACEHOLDER = 'Untitled step'

export const StepRow = ({ coords, projectId, allSteps, readOnly, catalog }: StepRowProps) => {
  const { step } = coords
  const moves = useStepMoves(projectId, coords)
  const sortable = useSortable({ id: step.id, disabled: readOnly })
  const sortableStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  }
  const update = useUpdateStep(projectId)
  const remove = useDeleteStep(projectId)
  const createChild = useCreateStep(projectId)
  const insertRef = useInsertProjectReference(projectId)
  const refreshRef = useRefreshFromSource(projectId)
  const detachMat = useDetachMaterial(projectId)
  const detachTool = useDetachTool(projectId)
  const confirm = useConfirm()
  const updateSm = useUpdateStepMaterial(projectId)

  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(step.title)
  const [descDraft, setDescDraft] = useState(step.description ?? '')
  const [addingChild, setAddingChild] = useState(false)
  const [childTitleDraft, setChildTitleDraft] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false)
  const [depsPickerOpen, setDepsPickerOpen] = useState(false)

  // Sync drafts when the underlying step changes (e.g. after refetch).
  useEffect(() => {
    if (!editingTitle) setTitleDraft(step.title)
  }, [step.title, editingTitle])

  useEffect(() => {
    setDescDraft(step.description ?? '')
  }, [step.description])

  const isAction = step.kind === 'ACTION'
  const isProjectReference = step.kind === 'PROJECT_REFERENCE'
  // Source can be deleted out from under us — referenced_project_id FK uses ON DELETE SET NULL.
  // When it's gone there's nothing to refresh against, so the affordance hides itself.
  const canRefresh = isProjectReference && step.referencedProjectId !== null
  const milestoneEligible = step.kind === 'SECTION' || step.kind === 'PROJECT_REFERENCE'
  const canConvertActionToSection =
    isAction && step.materials.length === 0 && step.tools.length === 0
  const canConvertSectionToAction = step.kind === 'SECTION' && step.children.length === 0

  const saveTitle = () => {
    setEditingTitle(false)
    const next = titleDraft.trim()
    if (next && next !== step.title) {
      update.mutate(
        { stepId: step.id, data: { title: next } },
        { onError: () => toast.error('Failed to save title') }
      )
    } else if (!next) {
      setTitleDraft(step.title)
    }
  }

  const saveDescription = () => {
    const next = descDraft
    if (next !== (step.description ?? '')) {
      update.mutate(
        { stepId: step.id, data: { description: next } },
        { onError: () => toast.error('Failed to save description') }
      )
    }
  }

  const handleDelete = async () => {
    const childCount = step.children.length
    const description =
      childCount > 0
        ? `This will also delete ${childCount} child step${childCount === 1 ? '' : 's'}.`
        : undefined
    const ok = await confirm({
      title: `Delete "${step.title}"?`,
      description,
      confirmText: 'Delete',
      destructive: true,
    })
    if (!ok) return
    remove.mutate(step.id, {
      onError: () => toast.error('Failed to delete step'),
    })
  }

  const submitChild = () => {
    const next = childTitleDraft.trim()
    if (!next) {
      setAddingChild(false)
      setChildTitleDraft('')
      return
    }
    createChild.mutate(
      { parentStepId: step.id, title: next },
      {
        onSuccess: () => {
          setAddingChild(false)
          setChildTitleDraft('')
        },
        onError: () => toast.error('Failed to add child step'),
      }
    )
  }

  const convertKind = (kind: 'ACTION' | 'SECTION') => {
    update.mutate(
      { stepId: step.id, data: { kind } },
      {
        onError: (err) => {
          const detail = (err as { detail?: string })?.detail
          toast.error(detail ?? `Could not convert to ${kind === 'ACTION' ? 'Action' : 'Section'}`)
        },
      }
    )
  }

  const toggleMilestone = () => {
    update.mutate(
      { stepId: step.id, data: { isMilestone: !step.isMilestone } },
      {
        onError: (err) => {
          const detail = (err as { detail?: string })?.detail
          toast.error(detail ?? 'Could not change milestone marker')
        },
      }
    )
  }

  const handleRefreshConfirm = () => {
    refreshRef.mutate(step.id, {
      onSuccess: () => {
        setRefreshDialogOpen(false)
        toast.success('Snapshot refreshed from source')
      },
      onError: (err) => {
        const apiErr = err as { status?: number; detail?: string }
        // Phase 6: source visibility recheck. Cross-author refs 403 when the source isn't PUBLIC
        // anymore — keep the dialog closed since the existing snapshot is intact and there's
        // nothing for the user to do here except wait for the source author to flip it back.
        if (apiErr.status === 403) {
          setRefreshDialogOpen(false)
          toast.error(
            apiErr.detail ??
              'Source Project is no longer publicly accessible — refresh is unavailable'
          )
          return
        }
        toast.error(apiErr.detail ?? 'Refresh failed')
      },
    })
  }

  return (
    <li
      ref={sortable.setNodeRef}
      style={sortableStyle}
      className={cn(
        'group border-l-2 pl-4',
        isProjectReference ? 'border-primary/60 bg-primary/5 rounded-r-md' : 'border-border',
        sortable.isDragging && 'opacity-40'
      )}
    >
      <div className="space-y-2 py-2">
        <div className="flex items-start justify-between gap-2">
          {!readOnly && (
            <StepDragHandle
              listeners={sortable.listeners}
              attributes={sortable.attributes}
              disabled={coords.siblings.length <= 1}
              className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[dragging=true]:opacity-100"
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <StepKindBadge
                kind={step.kind}
                isMilestone={step.isMilestone}
                referencedProjectTitle={step.referencedProjectTitle}
              />
              {/* Phase 6: cross-author source got demoted from PUBLIC since the snapshot. */}
              {isProjectReference && step.referencedProjectIsAccessible === false && (
                <span
                  className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400"
                  title="The source author has restricted this Project. The existing snapshot still works, but refresh-from-source isn't available."
                >
                  Source no longer publicly accessible
                </span>
              )}
              {step.dependsOn.length > 0 && (
                <DependencyBadge
                  count={step.dependsOn.length}
                  onClick={readOnly ? undefined : () => setDepsPickerOpen(true)}
                />
              )}
            </div>
            {readOnly ? (
              <h3 className="text-base font-medium">{step.title || TITLE_PLACEHOLDER}</h3>
            ) : editingTitle ? (
              <Input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    saveTitle()
                  } else if (e.key === 'Escape') {
                    setTitleDraft(step.title)
                    setEditingTitle(false)
                  }
                }}
                className="font-medium"
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditingTitle(true)}
                className="text-left text-base font-medium hover:underline"
              >
                {step.title || TITLE_PLACEHOLDER}
              </button>
            )}
          </div>
          {!readOnly && (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={moves.moveUp}
                disabled={!moves.canMoveUp}
                aria-label="Move step up"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={moves.moveDown}
                disabled={!moves.canMoveDown}
                aria-label="Move step down"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={moves.outdent}
                disabled={!moves.canOutdent}
                aria-label="Outdent step"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={moves.indent}
                disabled={!moves.canIndent}
                aria-label="Indent step"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1"
                onClick={() => setAddingChild(true)}
                disabled={addingChild}
                aria-label="Add child step"
              >
                <Plus className="h-4 w-4" />
                Child
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="More step actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canConvertActionToSection && (
                    <DropdownMenuItem onClick={() => convertKind('SECTION')}>
                      Convert to Section
                    </DropdownMenuItem>
                  )}
                  {canConvertSectionToAction && (
                    <DropdownMenuItem onClick={() => convertKind('ACTION')}>
                      Convert to Action
                    </DropdownMenuItem>
                  )}
                  {milestoneEligible && (
                    <DropdownMenuItem onClick={toggleMilestone}>
                      {step.isMilestone ? 'Unmark milestone' : 'Mark as milestone'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setDepsPickerOpen(true)}>
                    Set dependencies
                    {step.dependsOn.length > 0 && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({step.dependsOn.length})
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setPickerOpen(true)}>
                    Insert another Project here
                  </DropdownMenuItem>
                  {canRefresh && (
                    <DropdownMenuItem onClick={() => setRefreshDialogOpen(true)}>
                      <RefreshCw className="mr-2 h-3 w-3" />
                      Refresh from source
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={handleDelete}
                disabled={remove.isPending}
                aria-label="Delete step"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {readOnly ? (
          step.description && (
            <MarkdownContent value={step.description} className="text-sm text-muted-foreground" />
          )
        ) : (
          <MarkdownEditor
            value={descDraft}
            onChange={setDescDraft}
            onBlur={saveDescription}
            placeholder="Describe this step (optional)"
            rows={2}
            compact
          />
        )}

        {addingChild && (
          <div className="flex gap-2">
            <Input
              autoFocus
              placeholder="New child step title"
              value={childTitleDraft}
              onChange={(e) => setChildTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitChild()
                } else if (e.key === 'Escape') {
                  setAddingChild(false)
                  setChildTitleDraft('')
                }
              }}
            />
            <Button size="sm" onClick={submitChild} disabled={createChild.isPending}>
              Add
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAddingChild(false)
                setChildTitleDraft('')
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Materials and tools UI is rendered only on Actions — schema-level invariant. */}
        {isAction && (step.materials.length > 0 || !readOnly) && (
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Materials
              </span>
              {!readOnly && (
                <MaterialPicker
                  stepId={step.id}
                  projectId={projectId}
                  catalog={catalog.materials}
                  attachedIds={step.materials.map((m) => m.catalogMaterialId)}
                />
              )}
            </div>
            {step.materials.length === 0 ? (
              readOnly ? null : (
                <p className="text-xs text-muted-foreground">No materials attached.</p>
              )
            ) : (
              <ul className="space-y-1">
                {step.materials.map((sm) => {
                  const material = catalog.materials.find((m) => m.id === sm.catalogMaterialId)
                  if (!material) return null
                  return (
                    <li
                      key={sm.id}
                      className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2 py-1 text-sm"
                    >
                      <span className="flex-1 truncate">{material.name}</span>
                      {readOnly ? (
                        <span className="text-xs text-muted-foreground">
                          {sm.quantity} {material.unit} · $
                          {(material.unitPriceCents / 100).toFixed(2)}/{material.unit}
                        </span>
                      ) : (
                        <>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            defaultValue={String(sm.quantity)}
                            onBlur={(e) => {
                              const next = Number(e.target.value)
                              if (!Number.isFinite(next) || next <= 0) {
                                toast.error('Quantity must be greater than 0')
                                e.target.value = String(sm.quantity)
                                return
                              }
                              if (next === sm.quantity) return
                              updateSm.mutate(
                                { stepMaterialId: sm.id, data: { quantity: next } },
                                { onError: () => toast.error('Failed to update quantity') }
                              )
                            }}
                            className="h-7 w-20 text-xs"
                          />
                          <span className="text-xs text-muted-foreground">{material.unit}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() =>
                              detachMat.mutate(sm.id, {
                                onError: () => toast.error('Failed to detach'),
                              })
                            }
                            aria-label="Detach material"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        {isAction && (step.tools.length > 0 || !readOnly) && (
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Tools
              </span>
              {!readOnly && (
                <ToolPicker
                  stepId={step.id}
                  projectId={projectId}
                  catalog={catalog.tools}
                  attachedIds={step.tools.map((t) => t.catalogToolId)}
                />
              )}
            </div>
            {step.tools.length === 0 ? (
              readOnly ? null : (
                <p className="text-xs text-muted-foreground">No tools attached.</p>
              )
            ) : (
              <ul className="flex flex-wrap gap-1">
                {step.tools.map((st) => {
                  const tool = catalog.tools.find((t) => t.id === st.catalogToolId)
                  if (!tool) return null
                  return (
                    <li
                      key={st.id}
                      className="inline-flex items-center gap-1 rounded-md border bg-muted/30 px-2 py-1 text-xs"
                    >
                      <span>{tool.name}</span>
                      <span className="text-muted-foreground">
                        ${(tool.unitPriceCents / 100).toFixed(2)}
                      </span>
                      {!readOnly && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            detachTool.mutate(st.id, {
                              onError: () => toast.error('Failed to detach'),
                            })
                          }
                          aria-label="Detach tool"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        {isAction && (
          <StepPhotoGrid
            projectId={projectId}
            stepId={step.id}
            photos={step.photos ?? []}
            readOnly={readOnly}
          />
        )}

        {step.isMilestone && <MilestoneBomStrip step={step} catalog={catalog} />}
      </div>

      {step.children.length > 0 && (
        <ul className="ml-2 space-y-1">
          <SortableContext
            items={step.children.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {step.children.map((child, childIndex) => (
              <StepRow
                key={child.id}
                coords={{
                  step: child,
                  parent: step,
                  siblings: step.children,
                  index: childIndex,
                  grandparentId: coords.parent?.id ?? null,
                  parentSiblings: coords.siblings,
                  parentIndex: coords.index,
                }}
                projectId={projectId}
                allSteps={allSteps}
                readOnly={readOnly}
                catalog={catalog}
              />
            ))}
          </SortableContext>
        </ul>
      )}

      {!readOnly && (
        <>
          <DependencyPicker
            open={depsPickerOpen}
            onOpenChange={setDepsPickerOpen}
            projectSteps={allSteps}
            stepId={step.id}
            stepTitle={step.title}
            currentDependsOn={step.dependsOn}
            pending={update.isPending}
            onConfirm={(dependsOn) =>
              update.mutate(
                { stepId: step.id, data: { dependsOn } },
                {
                  onSuccess: () => {
                    setDepsPickerOpen(false)
                    toast.success(
                      dependsOn.length === 0
                        ? 'Cleared dependencies'
                        : `Set ${dependsOn.length} ${dependsOn.length === 1 ? 'dependency' : 'dependencies'}`
                    )
                  },
                  onError: (err) => {
                    const apiErr = err as { status?: number; detail?: string }
                    toast.error(apiErr.detail ?? 'Could not save dependencies')
                  },
                }
              )
            }
          />
          <ProjectPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            currentProjectId={projectId}
            currentProjectSteps={allSteps}
            pending={insertRef.isPending}
            onSelectOwn={(p) =>
              insertRef.mutate(
                { stepId: step.id, sourceProjectId: p.id },
                {
                  onSuccess: () => {
                    setPickerOpen(false)
                    toast.success(`Inserted "${p.title}" as a Project reference`)
                  },
                  onError: (err) => {
                    const apiErr = err as { status?: number; detail?: string }
                    toast.error(apiErr.detail ?? 'Could not insert Project reference')
                  },
                }
              )
            }
            onSelectFromBrowse={(p) =>
              insertRef.mutate(
                { stepId: step.id, sourceProjectId: p.id },
                {
                  onSuccess: () => {
                    setPickerOpen(false)
                    toast.success(`Inserted "${p.title}" by @${p.author.handle}`)
                  },
                  onError: (err) => {
                    const apiErr = err as { status?: number; detail?: string }
                    if (apiErr.status === 403) {
                      toast.error(
                        apiErr.detail ?? "That project isn't publicly referenceable anymore"
                      )
                    } else {
                      toast.error(apiErr.detail ?? 'Could not insert Project reference')
                    }
                  },
                }
              )
            }
          />
          {canRefresh && (
            <RefreshReferenceDialog
              open={refreshDialogOpen}
              onOpenChange={setRefreshDialogOpen}
              sourceTitle={step.referencedProjectTitle}
              pending={refreshRef.isPending}
              onConfirm={handleRefreshConfirm}
            />
          )}
        </>
      )}
    </li>
  )
}
